import { Grid, Typography, Container, Tooltip, IconButton, Stack } from '@mui/material';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandYoutube,
  IconChevronRight,
  IconPhone
} from '@tabler/icons-react';
import Link from 'next/link';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { gtagReportConversion } from 'utils/gtag';

interface ContactSectionProps {
  handlePopoverClose: () => void;
}

const LocationIcon = () => {
  const theme = useTheme();

  return (
    <svg width="22" height="24" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 1C7.686 1 5 3.686 5 7C5 11.286 11 18 11 18C11 18 17 11.286 17 7C17 3.686 14.314 1 11 1ZM11 4.85742C12.184 4.85742 13.1426 5.816 13.1426 7C13.1426 8.183 12.183 9.14258 11 9.14258C9.817 9.14258 8.85742 8.184 8.85742 7C8.85742 5.816 9.816 4.85742 11 4.85742ZM6.48047 15.4355C3.23147 16.0955 1 17.442 1 19C1 21.209 5.477 23 11 23C16.523 23 21 21.209 21 19C21 17.442 18.7685 16.0965 15.5195 15.4355C15.0675 16.0965 14.6248 16.7024 14.2168 17.2324C16.9488 17.6464 18.5662 18.512 18.9492 18.998C18.4342 19.652 15.696 20.998 11.002 20.998L11 21.002V21C6.306 21 3.56678 19.655 3.05078 19C3.43378 18.513 5.0502 17.6454 7.7832 17.2324C7.3752 16.7014 6.93247 16.0955 6.48047 15.4355Z"
        stroke={theme.palette.mode === 'dark' ? 'white' : '#135799'}
      />
    </svg>
  );
};

const ContactSection = ({ handlePopoverClose }: ContactSectionProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Container sx={{ padding: 4 }}>
      <Grid container spacing={2}>
        <Grid
          size={{ md: 3 }}
          sx={{
            paddingRight: '16px',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.grey[300]}`
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'CONTACT.US' })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid
                container
                spacing={2}
                sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', flexWrap: 'nowrap' }}
              >
                <Grid>
                  <IconButton
                    size="large"
                    disableRipple
                    aria-label="Phone"
                    sx={{
                      alignSelf: 'center',
                      color: theme.palette.mode === 'dark' ? 'white' : '#135799',
                      padding: 0,
                      '&:hover': {
                        color: '#135799'
                      }
                    }}
                  >
                    <Link href="tel:+90 (212) 295 22 30">
                      <IconPhone />
                    </Link>
                  </IconButton>
                </Grid>
                <Grid size={{ xs: 10 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      wordBreak: 'break-word'
                    }}
                  >
                    {formatMessage({ id: 'CONTACT.SUBTITLE' })}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : '#ED731D',
                      marginTop: 2,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  >
                    <Link
                      href="/contact-us"
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onClick={() => gtagReportConversion(undefined, 'AW-370956326/rhnPCP6nmbYaEKaw8bAB')}
                    >
                      {formatMessage({ id: 'CONTACT.US2' })}
                      <IconChevronRight />
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          size={{ md: 3 }}
          sx={{
            paddingRight: '16px',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.grey[300]}`
          }}
        >
          <Grid container spacing={2} pl={1}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'CONTACT.DOMESTIC' })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid
                container
                sx={{
                  flexWrap: 'nowrap',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  '&:hover .text': {
                    color: theme.palette.mode === 'dark' ? '#ED731D' : '#135799'
                  }
                }}
              >
                <Grid>
                  <LocationIcon />
                </Grid>
                <Grid>
                  <Typography
                    variant="body2"
                    className="text"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      marginLeft: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Link href="/contact/gebze" onClick={handlePopoverClose}>
                      {formatMessage({ id: 'CONTACT.GEBZE' })}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid
                container
                sx={{
                  flexWrap: 'nowrap',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  '&:hover .text': {
                    color: theme.palette.mode === 'dark' ? '#ED731D' : '#135799'
                  }
                }}
              >
                <Grid>
                  <LocationIcon />
                </Grid>
                <Grid>
                  <Typography
                    variant="body2"
                    className="text"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      marginLeft: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Link href="/contact/izmir" onClick={handlePopoverClose}>
                      {formatMessage({ id: 'CONTACT.IZMIR' })}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          size={{ md: 3 }}
          sx={{
            paddingRight: '16px',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.grey[300]}`
          }}
        >
          <Grid container spacing={2} pl={1}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'CONTACT.ABROAD' })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid
                container
                sx={{
                  flexWrap: 'nowrap',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  '&:hover .text': {
                    color: theme.palette.mode === 'dark' ? '#ED731D' : '#135799'
                  }
                }}
              >
                <Grid>
                  <LocationIcon />
                </Grid>
                <Grid>
                  <Typography
                    variant="body2"
                    className="text"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      marginLeft: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Link href="/contact/america" onClick={handlePopoverClose}>
                      {formatMessage({ id: 'CONTACT.AMERICA' })}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid
                container
                sx={{
                  flexWrap: 'nowrap',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  '&:hover .text': {
                    color: theme.palette.mode === 'dark' ? '#ED731D' : '#135799'
                  }
                }}
              >
                <Grid>
                  <LocationIcon />
                </Grid>
                <Grid>
                  <Typography
                    variant="body2"
                    className="text"
                    sx={{
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                      marginLeft: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Link href="/contact/asia" onClick={handlePopoverClose}>
                      {formatMessage({ id: 'CONTACT.ASIA' })}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ md: 3 }}>
          <Grid pl={1}>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: 16, md: 20 },
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'start',
                color: theme.palette.mode === 'dark' ? 'white' : '#135799'
              }}
            >
              {formatMessage({ id: 'SOCIAL.MEDIA' })}
            </Typography>
          </Grid>
          <Grid>
            <Stack direction="row" alignItems="start" justifyContent="start" spacing={{ xs: 3, md: 1.5, lg: 2 }} padding={2}>
              <Stack direction="column" alignItems="start" justifyContent="start" spacing={{ xs: 3, md: 1.5, lg: 2 }}>
                <Tooltip title="LinkedIn">
                  <Link href="https://www.linkedin.com/company/andevos/" target="_blank">
                    <IconButton
                      size="large"
                      disableRipple
                      aria-label="LinkedIn"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                        padding: 0,
                        margin: '4px 0',
                        '&:hover': {
                          color: '#0077B5'
                        }
                      }}
                    >
                      <IconBrandLinkedin />
                    </IconButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Instagram">
                  <Link href="https://www.instagram.com/andevosbilgiteknolojileri/" target="_blank">
                    <IconButton
                      size="large"
                      disableRipple
                      aria-label="Instagram"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                        padding: 0,
                        margin: '4px 0',
                        '&:hover': {
                          color: '#E4405F'
                        }
                      }}
                    >
                      <IconBrandInstagram />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
              <Stack direction="column" alignItems="start" justifyContent="start" spacing={{ xs: 3, md: 1.5, lg: 2 }}>
                <Tooltip title="Facebook">
                  <Link href="https://www.facebook.com/andevosbt" target="_blank">
                    <IconButton
                      size="large"
                      disableRipple
                      aria-label="Facebook"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                        padding: 0,
                        margin: '4px 0',
                        '&:hover': {
                          color: '#1877F2'
                        }
                      }}
                    >
                      <IconBrandFacebook />
                    </IconButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Youtube">
                  <Link href="https://www.youtube.com/@AndevosBilgiTeknolojileri" target="_blank">
                    <IconButton
                      size="large"
                      disableRipple
                      aria-label="Youtube"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                        padding: 0,
                        margin: '4px 0',
                        '&:hover': {
                          color: '#FF0000'
                        }
                      }}
                    >
                      <IconBrandYoutube />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactSection;
